import _ from 'lodash'
import { jwtDecode } from 'jwt-decode'
import {
  setPicture,
  setGlimpseApiToken,
  setFunnelUser,
  getLastOutlookEventSyncDate,
  getLastOutlookMessageSyncDate,
  getOutlookEventDeltaToken,
  getOutlookMessageDeltaToken,
  getFunnelUser,
  getPicture,
  clearStore,
  getGlimpseApiToken,
} from '../../server/store'

import store from '../store'
import Auth from '../auth'

import { getDurationInHours } from '../../server/utils/date'
import { logError } from '../../utils/logger'
import config from '../../config'
import * as authService from '../../entities/auth/service'
import { enrichSession } from '../../entities/session/service'
import { fetchUserPicture } from '../../entities/userSettings/service'
import { isLogoutInFlight } from '../thunks/logoutState'

const { MAX_ELAPSED_HOURS_TO_SYNC_ALL } = config

const LEGACY_AUTH0_USER_CLAIM = 'https://keystonestrategy.io/user'

/** Funnel person ids are MongoDB ObjectIds (24 hex). Entra oid is a UUID. */
export const isFunnelPersonObjectId = id => /^[a-f\d]{24}$/i.test(String(id || '').trim())

/** True when stored user._id is still Entra oid, not the Funnel MongoDB person id. */
export const needsFunnelPersonIdEnrichment = user => {
  const id = String(user?._id || '').trim()
  if (!id) return true
  return !isFunnelPersonObjectId(id)
}

/** Align with backend mapEntraClaimsToUser (security module). */
export const mapEntraClaimsToUser = payload => {
  if (!payload || typeof payload !== 'object') return null
  if (payload.user && typeof payload.user === 'object' && payload.user._id) {
    return payload.user
  }
  const email = payload.preferred_username || payload.email || ''
  const given = payload.given_name || ''
  const family = payload.family_name || ''
  const fullName =
    payload.name ||
    [given, family]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    email
  const id = payload.oid || payload.sub
  if (!id) return null
  return {
    _id: id,
    startDate: '',
    repliconId: null,
    email,
    firstName: given,
    fullName,
    outlookId: payload.oid || '',
    position: '',
    employeeType: '',
  }
}

const getAndSetGlimpseApiToken = async (auth0Client, client, silent, popup) => {
  const glimpseApiToken = await authService.getGlimpseApiTokenMutation(auth0Client, silent, popup)
  const user = await getAndSetDataFromToken(glimpseApiToken, client)
  return user
}

export const getAndSetDataFromToken = async (glimpseApiToken, client) => {
  if (glimpseApiToken) {
    setGlimpseApiToken(glimpseApiToken)
    clearReauthRequired()
  } else return null
  let decodedToken
  try {
    decodedToken = jwtDecode(glimpseApiToken)
  } catch {
    decodedToken = null
  }
  if (!decodedToken || typeof decodedToken !== 'object') {
    throw new Error('A valid profile for the user could not be found. Please contact support.')
  }
  const legacyUser = decodedToken[LEGACY_AUTH0_USER_CLAIM]
  const user = legacyUser || mapEntraClaimsToUser(decodedToken)

  if (!user || !user._id) {
    throw new Error('A valid profile for the user could not be found. Please contact support.')
  }

  let mergedUser = user
  if (needsFunnelPersonIdEnrichment(user) || !user.repliconId) {
    const enriched = await enrichSession(client)
    if (enriched) {
      mergedUser = { ...user, ...enriched }
    }
  }

  const picture = await savePictureToLocalStorage(client)

  setFunnelUser(mergedUser)
  return { glimpseApiToken, person: mergedUser, picture }
}

/**
 * Ensures local funnel person has Funnel-backed ids (e.g. after upgrade from pre-enrichment sessions).
 * @param {import('@apollo/client').ApolloClient} client
 * @param {object} auth0 MSAL adapter
 */
export const ensureFunnelUserEnriched = async (client, auth0) => {
  const person = getFunnelUser()
  if (person && !needsFunnelPersonIdEnrichment(person) && person.repliconId) return person
  await getLatestGlimpseToken(auth0)
  const enriched = await enrichSession(client)
  if (!enriched) return person
  const merged = { ...person, ...enriched }
  setFunnelUser(merged)
  return merged
}

const savePictureToLocalStorage = async client => {
  const response = await fetchUserPicture(client)
  if (response.hasPicture) {
    const picture = `data:${response.contentType};base64,${response.picture}`
    setPicture(picture)
    return picture
  }
  return null
}

export const loginService = async (auth0Client, client, silent, popup) => {
  await deleteAllData({ preserveMsalCache: true })
  const data = await getAndSetGlimpseApiToken(auth0Client, client, silent, popup)
  if (!data) return null
  const { person, picture } = data

  return {
    ...person,
    picture,
  }
}

// will be removed in next commit
const isOutlookDeltaTokensValid = () => {
  const lastEventSyncDate = getLastOutlookEventSyncDate()
  const lastMessageSyncDate = getLastOutlookMessageSyncDate()
  const lastSyncDate =
    lastEventSyncDate < lastMessageSyncDate ? lastEventSyncDate : lastMessageSyncDate
  const doesDeltaTokenExist =
    !_.isEmpty(getOutlookEventDeltaToken()) && !_.isEmpty(getOutlookMessageDeltaToken())
  if (!lastSyncDate || !doesDeltaTokenExist) return false
  const timeElapsed = getDurationInHours(lastSyncDate, new Date())
  return Number(timeElapsed) < MAX_ELAPSED_HOURS_TO_SYNC_ALL
}

export const calcIsUserLoggedIn = async auth0 => {
  const token = await getLatestGlimpseToken(auth0)
  const isLoggedIn = !!token && (config.IS_TEST_E2E || isOutlookDeltaTokensValid())
  const person = getFunnelUser()
  const picture = getPicture()
  return {
    userInfo: {
      ...person,
      picture,
    },
    isLoggedIn,
  }
}

/**
 * @param {{ preserveMsalCache?: boolean }} [options] - Keep MSAL browser keys so `logoutRedirect` can sign out Entra.
 */
export const deleteAllData = async (options = {}) => {
  const { preserveMsalCache = false } = options
  if (preserveMsalCache) {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (!key.toLowerCase().includes('msal')) localStorage.removeItem(key)
    })
  } else {
    localStorage.clear()
  }
  clearStore()
  clearReauthRequired()
}

const TOKEN_ACQUISITION_TIMEOUT_MS = 10000
const TOKEN_ACQUISITION_TIMEOUT_MESSAGE = 'Token acquisition timed out'

const fetchNewtokenPromiseQueue = []

const resolveFetchNewtokenPromiseQueue = (token, error = null) => {
  const waiters = fetchNewtokenPromiseQueue.splice(0, fetchNewtokenPromiseQueue.length)
  for (const waiter of waiters) {
    if (waiter === null) continue
    if (error !== null) waiter.reject(error)
    else waiter.resolve(token)
  }
}

const withTimeout = promise =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(TOKEN_ACQUISITION_TIMEOUT_MESSAGE))
    }, TOKEN_ACQUISITION_TIMEOUT_MS)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })

const fetchNewToken = async (auth0, options) => {
  if (fetchNewtokenPromiseQueue.length > 0)
    return new Promise((resolve, reject) => fetchNewtokenPromiseQueue.push({ resolve, reject }))

  fetchNewtokenPromiseQueue.push(null)

  try {
    const token = await withTimeout(
      options ? auth0.getAccessTokenSilently(options) : auth0.getAccessTokenSilently(),
    )
    resolveFetchNewtokenPromiseQueue(token)
    return token
  } catch (error) {
    logError(error, 'redux > services > auth > fetchNewToken')
    resolveFetchNewtokenPromiseQueue(null, error)
    throw error
  }
}

const isTokenStillValid = token => {
  if (!token) return false
  let decodedToken
  try {
    decodedToken = jwtDecode(token)
  } catch {
    return false
  }
  if (!decodedToken || !decodedToken.exp) return false
  const now = Math.floor(Date.now() / 1000)
  return now <= decodedToken.exp - 60
}

let reauthRequired = false

export const isReauthRequired = () => reauthRequired

const markReauthRequired = ({ skipFreshnessCheck = false } = {}) => {
  if (reauthRequired) return
  if (!skipFreshnessCheck && isTokenStillValid(getGlimpseApiToken())) return
  reauthRequired = true
  store.dispatch(Auth.reauthRequired())
}

/**
 * Marks the session as recovered: any path that has just established a
 * fresh, valid token (silent refresh, forced refresh, or an interactive
 * login) or torn the session down (deleteAllData) should call this so a
 * stale flag can't outlive the session that set it.
 */
export const clearReauthRequired = () => {
  if (!reauthRequired) return
  reauthRequired = false
  store.dispatch(Auth.reauthResolved())
}

const resolveGlimpseToken = async (auth0, { fallbackToStoredToken }) => {
  const tokenFromStore = getGlimpseApiToken()
  if (!tokenFromStore) return null
  if (isTokenStillValid(tokenFromStore)) {
    clearReauthRequired()
    return tokenFromStore
  }
  store.dispatch(Auth.loginRequest())
  try {
    const tokenFromAuth0 = await fetchNewToken(auth0, { silentOnly: true })
    setGlimpseApiToken(tokenFromAuth0)
    clearReauthRequired()
    return tokenFromAuth0
  } catch (e) {
    if (e?.interactionRequired || e?.message === TOKEN_ACQUISITION_TIMEOUT_MESSAGE) {
      markReauthRequired()
      return null
    }
    return fallbackToStoredToken ? tokenFromStore : null
  }
}

/**
 * Answers "is there a session?" for presence-only consumers (calcIsUserLoggedIn,
 * DataSync, SocketIO).
 *
 * On a transient refresh failure, falls back to the stored token even if it's
 * expired: for these consumers a `null` reads as "session closed" and skips
 * syncs or leaves the socket unauthorized.
 *
 * Do NOT use this as a request credential — use {@link getValidGlimpseToken}
 * for that.
 *
 * @param {object} auth0 - The MSAL adapter.
 * @returns {Promise<string|null>}
 */
export const getLatestGlimpseToken = auth0 =>
  resolveGlimpseToken(auth0, { fallbackToStoredToken: true })

/**
 * Returns the credential to send with a request (Apollo's authLink).
 *
 * Never returns a token the backend is certain to reject: if the refresh
 * fails, returns `null` and the request goes out without an Authorization
 * header.
 *
 * @param {object} auth0 - The MSAL adapter.
 * @returns {Promise<string|null>}
 */
export const getValidGlimpseToken = auth0 =>
  resolveGlimpseToken(auth0, { fallbackToStoredToken: false })

/**
 * Forces an unconditional, strictly silent refresh (skips the expiry check).
 * Used by Apollo's error link when the backend has already rejected the token.
 *
 * Marks the session as needing interactive reauthentication on both
 * `interactionRequired` and a timed-out acquisition, before returning or
 * throwing per the branches below. That mark bypasses the stored-token
 * freshness check: the backend has already rejected the token we hold, so an
 * unexpired `exp` is no evidence the session is healthy.
 *
 * @param {object} auth0 - The MSAL adapter.
 * @returns {Promise<string|null>} The refreshed token on success; `null` when
 *   interaction is required (the session can't be recovered silently) or a
 *   logout is already in flight (must not resurrect the token it just cleared).
 * @throws {Error} Any other failure (e.g. network, or a timed-out acquisition)
 *   — transient, not "no session".
 */
export const forceRefreshGlimpseToken = async auth0 => {
  if (isLogoutInFlight()) return null
  let token
  try {
    token = await fetchNewToken(auth0, { silentOnly: true })
  } catch (e) {
    if (e?.interactionRequired || e?.message === TOKEN_ACQUISITION_TIMEOUT_MESSAGE) {
      markReauthRequired({ skipFreshnessCheck: true })
    }
    if (e?.interactionRequired) return null
    throw e
  }
  if (!token) return null
  setGlimpseApiToken(token)
  clearReauthRequired()
  return token
}
